import {
  Room,
  RoomEvent,
  Track,
  DisconnectReason,
  type RemoteParticipant,
  type LocalTrack,
  LocalAudioTrack,
  createLocalAudioTrack,
} from 'livekit-client';
import {
  RoomState,
  MicrophoneState,
  type DPRoomClientOptions,
  type DPRoomClientEvents,
  type AudioConfig,
  type DataChannelMessage,
  type ConversationMessage,
  type DPData,
  type DriverConfig,
  type AppInfo,
  type InitMessageOptions,
  type RoomErrorData,
  type ClientLifecycle,
  type LogLevel,
  type ReconnectPolicy,
  type ReconnectFailReason,
  type ReconnectAttemptInfo,
  type ReconnectSuccessInfo,
  type PerformanceStats,
} from './types';
import { RoomStateMachine } from './state-machine';
import { createInitMessage } from './message-builder';
import { ApiService, ApiError } from './api-service';
import { ChromaKeyRenderer } from './ChromaKeyRenderer';
import { installVP9SDPInterceptor, autoDetectAlpha } from './VP9AlphaHelper';
import {
  READY_TIMEOUT,
  READY_DELAY,
  DEFAULT_AUDIO_CONFIG,
  DEFAULT_RECONNECT_MAX_ATTEMPTS,
  DEFAULT_RECONNECT_BASE_DELAY_MS,
  DEFAULT_RECONNECT_MAX_DELAY_MS,
  DEFAULT_RECONNECT_ATTEMPT_TIMEOUT_MS,
  MESSAGE_SEND_MAX_RETRIES,
  MESSAGE_SEND_RETRY_DELAY_MS,
} from './constants';

const LOG_LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

const ROOM_ID_STORAGE_KEY = '__dp_sdk_room_id__';

declare const __SDK_VERSION__: string;
declare const __DEFAULT_BASE_URL__: string;
declare const __SDK_ENV__: string;
declare const __DEV__: boolean;

export class DPRoomClient {
  static readonly version: string = typeof __SDK_VERSION__ !== 'undefined' ? __SDK_VERSION__ : '1.0.0';
  static readonly env: string = typeof __SDK_ENV__ !== 'undefined' ? __SDK_ENV__ : 'prod';

  private static _saveRoomId(roomId: string): void {
    try { localStorage.setItem(ROOM_ID_STORAGE_KEY, roomId); } catch { /* SSR / 隐私模式 */ }
  }

  private static _clearRoomId(): void {
    try { localStorage.removeItem(ROOM_ID_STORAGE_KEY); } catch { /* SSR / 隐私模式 */ }
  }

  private static _loadRoomId(): string | null {
    try { return localStorage.getItem(ROOM_ID_STORAGE_KEY); } catch { return null; }
  }

  // ==================== 私有状态 ====================

  private _room: Room | null = null;
  private _logLevel: number;
  private _stateMachine: RoomStateMachine;
  private _audioConfig: Required<AudioConfig>;
  private _options: DPRoomClientOptions;
  private _audioTrack: LocalAudioTrack | null = null;
  private _micState: MicrophoneState = MicrophoneState.Idle;
  private _trackEndedHandler: (() => void) | null = null;

  private _lifecycle: ClientLifecycle = 'created';
  private _isConnected = false;
  private _isDriverOnline = false;
  private _isRoomReady = false;
  private _hasVideoTrack = false;

  private _readyTimeout: ReturnType<typeof setTimeout> | null = null;
  private _readyDelay: ReturnType<typeof setTimeout> | null = null;
  private _readyTimeoutMs: number;
  private _readyTimeoutPolicy: 'resolve' | 'reject';
  private _readyConditions: { initAck: boolean; videoTrack: boolean };
  private _initAckReceived = false;

  private _lastInitArgs: {
    dpData: DPData;
    driverConfig: DriverConfig;
    appInfo: AppInfo;
    options: InitMessageOptions;
  } | null = null;

  private _listeners: { [K in keyof DPRoomClientEvents]?: DPRoomClientEvents[K][] } = {};

  private _containerEl: HTMLElement | null = null;
  private _chromaKeyRenderer: ChromaKeyRenderer | null = null;
  private _hiddenVideoEl: HTMLVideoElement | null = null;
  private _nativeVideoEl: HTMLVideoElement | null = null;
  private _alphaMode: 'pending' | 'native' | 'chroma-key' | null = null;
  private _apiService: ApiService | null = null;
  private _roomId: string | null = null;
  private _boundBeforeUnload: (() => void) | null = null;

  private _initTask: Promise<void> | null = null;
  private _connectTask: Promise<void> | null = null;
  private _connectPolicy: 'ignore' | 'queue' | 'cancel-previous';
  private _pendingConnect: { url: string; token: string; resolve: () => void; reject: (e: Error) => void } | null = null;

  private _reconnectPolicy: Required<Omit<ReconnectPolicy, 'tokenProvider'>> & { tokenProvider?: ReconnectPolicy['tokenProvider'] };
  private _isReconnecting = false;
  private _reconnectAbort: AbortController | null = null;
  private _lastTaskId: string | null = null;
  private _micWasActiveBeforeDisconnect = false;
  private _autoEnableMicrophone: boolean;
  private _lastConnectArgs: { url: string; token: string } | null = null;

  private _perfTimer: ReturnType<typeof setInterval> | null = null;
  private _perfPrev: {
    timestamp: number;
    videoBytesReceived: number;
    videoFramesDecoded: number;
    audioBytesReceived: number;
  } = { timestamp: 0, videoBytesReceived: 0, videoFramesDecoded: 0, audioBytesReceived: 0 };
  private _perfStats: PerformanceStats | null = null;

  // ==================== 构造函数 ====================

  constructor(options: DPRoomClientOptions = {}) {
    this._options = options;
    this._logLevel = LOG_LEVELS[options.logLevel ?? 'warn'];
    this._audioConfig = { ...DEFAULT_AUDIO_CONFIG, ...options.audioConfig };
    this._readyTimeoutMs = options.readyTimeoutMs ?? READY_TIMEOUT;
    this._readyTimeoutPolicy = options.readyTimeoutPolicy ?? 'resolve';
    this._readyConditions = {
      initAck: options.readyConditions?.initAck ?? true,
      videoTrack: options.readyConditions?.videoTrack ?? false,
    };
    this._connectPolicy = options.connectPolicy ?? 'ignore';
    this._autoEnableMicrophone = options.autoEnableMicrophone ?? true;

    const rp = options.reconnectPolicy ?? {};
    this._reconnectPolicy = {
      autoReconnect: rp.autoReconnect ?? true,
      fullReconnect: rp.fullReconnect ?? true,
      maxAttempts: rp.maxAttempts ?? DEFAULT_RECONNECT_MAX_ATTEMPTS,
      baseDelayMs: rp.baseDelayMs ?? DEFAULT_RECONNECT_BASE_DELAY_MS,
      maxDelayMs: rp.maxDelayMs ?? DEFAULT_RECONNECT_MAX_DELAY_MS,
      attemptTimeoutMs: rp.attemptTimeoutMs ?? DEFAULT_RECONNECT_ATTEMPT_TIMEOUT_MS,
      tokenProvider: rp.tokenProvider,
    };

    const resolvedBaseUrl = options.baseUrl
      || (typeof __DEFAULT_BASE_URL__ !== 'undefined' ? __DEFAULT_BASE_URL__ : '')
      || '';

    if (resolvedBaseUrl && options.accessToken) {
      this._apiService = new ApiService(
        resolvedBaseUrl,
        options.accessToken,
        options.requestPolicy,
      );
      this._log(
        'info',
        `API baseUrl: ${resolvedBaseUrl} (source: ${options.baseUrl ? 'custom' : 'built-in ' + DPRoomClient.env})`,
      );
    }

    this._stateMachine = new RoomStateMachine();
    this._stateMachine.onChange((newState, oldState) => {
      this._emit('state-change', newState, oldState);
      options.onStateChange?.(newState);
    });

    if (options.container) {
      this._containerEl =
        typeof options.container === 'string'
          ? document.querySelector<HTMLElement>(options.container)
          : options.container;
    }

    if (this._containerEl && options.chromaKey?.enabled) {
      this._initChromaKey(options);
    }

    this._boundBeforeUnload = () => {
      this._syncDestroy();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this._boundBeforeUnload);
    }

    if (this._containerEl) {
      this.on('track-subscribed', (track) => {
        if (!this._containerEl) return;
        if (track.kind === 'video') {
          if (this._chromaKeyRenderer && this._hiddenVideoEl) {
            (track as any).attach(this._hiddenVideoEl);
            this._alphaMode = 'pending';
            this._log('info', 'Detecting native VP9 alpha channel...');

            autoDetectAlpha(this._hiddenVideoEl, (hasNativeAlpha) => {
              if (hasNativeAlpha) {
                this._log('info', 'Native VP9 alpha detected — using transparent video');
                this._alphaMode = 'native';

                const chromaCanvas = this._chromaKeyRenderer?.getCanvas();
                if (chromaCanvas) chromaCanvas.style.display = 'none';

                const nv = document.createElement('video');
                nv.playsInline = true;
                nv.autoplay = true;
                nv.muted = false;
                nv.style.width = '100%';
                nv.style.height = '100%';
                nv.style.objectFit = 'contain';
                nv.style.display = 'block';
                nv.style.background = 'transparent';
                this._containerEl!.appendChild(nv);
                this._nativeVideoEl = nv;

                (track as any).attach(nv);
              } else {
                this._log('info', 'No native alpha — falling back to chroma key');
                this._alphaMode = 'chroma-key';
                this._chromaKeyRenderer!.start();
              }

              this._emit('alpha-mode-change', this._alphaMode as 'native' | 'chroma-key');
            });
          } else {
            (track as any).attach(this._containerEl);
            if (this._containerEl instanceof HTMLVideoElement) {
              this._containerEl.muted = false;
            }
          }
        } else if (track.kind === 'audio') {
          if (this._chromaKeyRenderer) {
            const audioEl = (track as any).attach() as HTMLAudioElement;
            audioEl.style.display = 'none';
            document.body.appendChild(audioEl);
          } else {
            (track as any).attach(this._containerEl);
            if (this._containerEl instanceof HTMLVideoElement) {
              this._containerEl.muted = false;
            }
          }
        }
      });
    }

    if (options.onReady) this.on('room-ready', options.onReady);
    if (options.onError) this.on('error', options.onError);

    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      if (!options.baseUrl && !options.container && !options.accessToken) {
        this._log(
          'warn',
          '建议至少配置 baseUrl+accessToken 或 container，否则需要手动管理全部流程。详见文档第 3 章。',
        );
      }
    }
  }

  // ==================== 公开属性 ====================

  get room(): Room | null {
    return this._room;
  }

  get lifecycle(): ClientLifecycle {
    return this._lifecycle;
  }

  get chromaKeyRenderer(): ChromaKeyRenderer | null {
    return this._chromaKeyRenderer;
  }

  get alphaMode(): 'pending' | 'native' | 'chroma-key' | null {
    return this._alphaMode;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  get isDriverOnline(): boolean {
    return this._isDriverOnline;
  }

  get isRoomReady(): boolean {
    return this._isRoomReady;
  }

  get state(): RoomState {
    return this._stateMachine.state;
  }

  get microphoneState(): MicrophoneState {
    return this._micState;
  }

  get currentFps(): number {
    return this._perfStats?.video?.fps ?? 0;
  }

  get performanceStats(): PerformanceStats | null {
    return this._perfStats;
  }

  // ==================== 性能监控 ====================

  /**
   * 启动性能监控，定时采集 WebRTC 指标并通过事件广播。
   * @param intervalMs 采样间隔(ms)，默认 1000
   */
  startPerformanceMonitor(intervalMs = 1000): void {
    this.stopPerformanceMonitor();
    this._perfPrev = { timestamp: 0, videoBytesReceived: 0, videoFramesDecoded: 0, audioBytesReceived: 0 };
    this._perfStats = null;

    this._perfTimer = setInterval(() => {
      this._samplePerformance();
    }, intervalMs);
    this._log('debug', 'Performance monitor started');
  }

  /** 停止性能监控 */
  stopPerformanceMonitor(): void {
    if (this._perfTimer !== null) {
      clearInterval(this._perfTimer);
      this._perfTimer = null;
      this._perfStats = null;
      this._perfPrev = { timestamp: 0, videoBytesReceived: 0, videoFramesDecoded: 0, audioBytesReceived: 0 };
      this._log('debug', 'Performance monitor stopped');
    }
  }

  /** @deprecated 请使用 startPerformanceMonitor() */
  startFpsMonitor(intervalMs = 1000): void {
    this.startPerformanceMonitor(intervalMs);
  }

  /** @deprecated 请使用 stopPerformanceMonitor() */
  stopFpsMonitor(): void {
    this.stopPerformanceMonitor();
  }

  /**
   * 按需获取性能快照（一次性采集，不需要开启 monitor）。
   */
  async getPerformanceStats(): Promise<PerformanceStats> {
    return this._collectStats();
  }

  private async _samplePerformance(): Promise<void> {
    const stats = await this._collectStats();
    this._perfStats = stats;
    this._emit('performance-report', stats);
    if (stats.video) {
      this._emit('fps-update', stats.video.fps);
    }
  }

  private async _collectStats(): Promise<PerformanceStats> {
    const result: PerformanceStats = {
      timestamp: performance.now(),
      video: null,
      audio: null,
      network: null,
    };

    if (!this._room) return result;

    const pcSet = this._getPeerConnections();
    if (pcSet.size === 0) return result;

    let videoBytes = 0;
    let videoFramesDecoded = 0;
    let videoFramesDropped = 0;
    let videoFramesReceived = 0;
    let videoPacketsLost = 0;
    let videoPacketsReceived = 0;
    let videoJitter = 0;
    let videoNackCount = 0;
    let videoPliCount = 0;
    let videoWidth = 0;
    let videoHeight = 0;
    let videoCodec: string | null = null;

    let audioBytes = 0;
    let audioPacketsLost = 0;
    let audioPacketsReceived = 0;
    let audioJitter = 0;
    let audioCodec: string | null = null;

    let rtt = 0;
    let candidateType = '';
    let hasVideo = false;
    let hasAudio = false;

    for (const pc of pcSet) {
      try {
        const stats = await pc.getStats();

        stats.forEach((report: any) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            hasVideo = true;
            videoBytes += report.bytesReceived || 0;
            videoFramesDecoded += report.framesDecoded || 0;
            videoFramesDropped += report.framesDropped || 0;
            videoFramesReceived += report.framesReceived || 0;
            videoPacketsLost += report.packetsLost || 0;
            videoPacketsReceived += report.packetsReceived || 0;
            videoJitter = report.jitter ?? videoJitter;
            videoNackCount += report.nackCount || 0;
            videoPliCount += report.pliCount || 0;
            if (report.frameWidth) videoWidth = report.frameWidth;
            if (report.frameHeight) videoHeight = report.frameHeight;

            if (!videoCodec && report.codecId) {
              const codecReport = stats.get(report.codecId);
              if (codecReport) videoCodec = codecReport.mimeType || null;
            }
          }

          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            hasAudio = true;
            audioBytes += report.bytesReceived || 0;
            audioPacketsLost += report.packetsLost || 0;
            audioPacketsReceived += report.packetsReceived || 0;
            audioJitter = report.jitter ?? audioJitter;

            if (!audioCodec && report.codecId) {
              const codecReport = stats.get(report.codecId);
              if (codecReport) audioCodec = codecReport.mimeType || null;
            }
          }

          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            rtt = report.currentRoundTripTime != null
              ? report.currentRoundTripTime * 1000
              : rtt;
            if (!candidateType && report.remoteCandidateId) {
              const remote = stats.get(report.remoteCandidateId);
              if (remote) candidateType = remote.candidateType || '';
            }
          }
        });
      } catch { /* skip */ }
    }

    const now = result.timestamp;
    const prev = this._perfPrev;
    const elapsed = prev.timestamp > 0 ? (now - prev.timestamp) / 1000 : 0;

    if (hasVideo) {
      const fps = elapsed > 0
        ? Math.round((videoFramesDecoded - prev.videoFramesDecoded) / elapsed)
        : 0;
      const bitrate = elapsed > 0
        ? Math.round(((videoBytes - prev.videoBytesReceived) * 8) / elapsed / 1000)
        : 0;

      result.video = {
        fps: Math.max(fps, 0),
        bitrate: Math.max(bitrate, 0),
        width: videoWidth,
        height: videoHeight,
        codec: videoCodec,
        framesDecoded: videoFramesDecoded,
        framesDropped: videoFramesDropped,
        framesReceived: videoFramesReceived,
        jitter: videoJitter,
        packetsLost: videoPacketsLost,
        packetsReceived: videoPacketsReceived,
        bytesReceived: videoBytes,
        nackCount: videoNackCount,
        pliCount: videoPliCount,
      };
    }

    if (hasAudio) {
      const bitrate = elapsed > 0
        ? Math.round(((audioBytes - prev.audioBytesReceived) * 8) / elapsed / 1000)
        : 0;

      result.audio = {
        bitrate: Math.max(bitrate, 0),
        codec: audioCodec,
        jitter: audioJitter,
        packetsLost: audioPacketsLost,
        packetsReceived: audioPacketsReceived,
        bytesReceived: audioBytes,
      };
    }

    if (rtt > 0 || candidateType) {
      result.network = {
        rtt: Math.round(rtt * 100) / 100,
        candidateType,
      };
    }

    this._perfPrev = {
      timestamp: now,
      videoBytesReceived: videoBytes,
      videoFramesDecoded: videoFramesDecoded,
      audioBytesReceived: audioBytes,
    };

    return result;
  }

  private _getPeerConnections(): Set<RTCPeerConnection> {
    const pcSet = new Set<RTCPeerConnection>();
    if (!this._room) return pcSet;
    const room = this._room as any;

    const explicit = [
      room?._pcManager?.subscriber?.pc,
      room?._pcManager?.publisher?.pc,
      room?.engine?.subscriber?.pc,
      room?.engine?.publisher?.pc,
      room?.engine?._pcManager?.subscriber?.pc,
      room?.engine?._pcManager?.publisher?.pc,
      room?.engine?.pcManager?.subscriber?.pc,
      room?.engine?.pcManager?.publisher?.pc,
      room?.engine?.client?.subscriber?.pc,
      room?.engine?.client?.publisher?.pc,
    ];
    for (const pc of explicit) {
      if (pc instanceof RTCPeerConnection) pcSet.add(pc);
    }

    if (pcSet.size === 0) {
      try { this._findPeerConnections(room, 0, pcSet); } catch { /* skip */ }
      if (pcSet.size === 0 && room?.engine) {
        try { this._findPeerConnections(room.engine, 0, pcSet); } catch { /* skip */ }
      }
    }

    return pcSet;
  }

  // ==================== 编解码器诊断 ====================

  private _findPeerConnections(obj: any, depth = 0, found = new Set<RTCPeerConnection>()): Set<RTCPeerConnection> {
    if (depth > 4 || !obj || typeof obj !== 'object') return found;
    try {
      if (obj instanceof RTCPeerConnection) { found.add(obj); return found; }
      for (const key of Object.keys(obj)) {
        try {
          const val = obj[key];
          if (val instanceof RTCPeerConnection) {
            found.add(val);
          } else if (val && typeof val === 'object' && !found.has(val)) {
            this._findPeerConnections(val, depth + 1, found);
          }
        } catch { /* skip */ }
      }
    } catch { /* skip */ }
    return found;
  }

  /**
   * Diagnose the current WebRTC codec and VP9 alpha status.
   * Call from browser console: `dpClient.getCodecStats()`
   */
  async getCodecStats(): Promise<{
    videoCodec: string | null;
    alphaMode: string | null;
    peerConnections: number;
    details: string[];
  }> {
    const result = {
      videoCodec: null as string | null,
      alphaMode: this._alphaMode,
      peerConnections: 0,
      details: [] as string[],
    };

    if (!this._room) {
      result.details.push('Room not connected');
      console.log('[DPRoomSDK][CODEC DIAG]', result);
      return result;
    }

    const pcSet = this._getPeerConnections();
    result.peerConnections = pcSet.size;
    result.details.push(`Found ${pcSet.size} PeerConnection(s)`);

    for (const pc of pcSet) {
      try {
        // SDP analysis
        const localSdp = pc.localDescription?.sdp;
        const remoteSdp = pc.remoteDescription?.sdp;
        if (localSdp) {
          const vp9Entries = localSdp.match(/a=rtpmap:\d+ VP9\/\d+/gi) || [];
          const h264Entries = localSdp.match(/a=rtpmap:\d+ H264\/\d+/gi) || [];
          result.details.push(`Local SDP: VP9×${vp9Entries.length}, H264×${h264Entries.length}`);
          // Check m=video line for codec order
          const mLine = localSdp.match(/m=video .+/);
          if (mLine) result.details.push(`Local m=video: ${mLine[0].substring(0, 120)}`);
        }
        if (remoteSdp) {
          const vp9Entries = remoteSdp.match(/a=rtpmap:\d+ VP9\/\d+/gi) || [];
          const h264Entries = remoteSdp.match(/a=rtpmap:\d+ H264\/\d+/gi) || [];
          result.details.push(`Remote SDP: VP9×${vp9Entries.length}, H264×${h264Entries.length}`);
          const mLine = remoteSdp.match(/m=video .+/);
          if (mLine) result.details.push(`Remote m=video: ${mLine[0].substring(0, 120)}`);
        }

        // RTP stats
        const stats = await pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const codecId = report.codecId;
            result.details.push(`inbound-rtp: bytes=${report.bytesReceived}, frames=${report.framesDecoded}, codecId=${codecId}`);
            if (codecId) {
              const cr = stats.get(codecId);
              if (cr) {
                result.videoCodec = cr.mimeType || null;
                result.details.push(`>>> ACTIVE CODEC: ${cr.mimeType} (pt=${cr.payloadType}, fmtp=${cr.sdpFmtpLine || 'N/A'})`);
              }
            }
          }
        });
      } catch (err) {
        result.details.push(`getStats error: ${err}`);
      }
    }

    // Summary
    console.log('%c[CODEC DIAG]', 'color:#f09; font-weight:bold', result);
    for (const d of result.details) {
      console.log(`  ${d}`);
    }
    return result;
  }

  // ==================== 事件系统 ====================

  on<K extends keyof DPRoomClientEvents>(event: K, handler: DPRoomClientEvents[K]): void {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event]!.push(handler);
  }

  off<K extends keyof DPRoomClientEvents>(event: K, handler: DPRoomClientEvents[K]): void {
    const list = this._listeners[event];
    if (!list) return;
    const idx = list.indexOf(handler);
    if (idx !== -1) list.splice(idx, 1);
  }

  once<K extends keyof DPRoomClientEvents>(event: K, handler: DPRoomClientEvents[K]): void {
    const wrapper = ((...args: unknown[]) => {
      this.off(event, wrapper as DPRoomClientEvents[K]);
      (handler as (...a: unknown[]) => void)(...args);
    }) as DPRoomClientEvents[K];
    this.on(event, wrapper);
  }

  private _emit<K extends keyof DPRoomClientEvents>(
    event: K,
    ...args: Parameters<DPRoomClientEvents[K]>
  ): void {
    const list = this._listeners[event];
    if (!list) return;
    for (const handler of [...list]) {
      try {
        (handler as (...a: unknown[]) => void)(...args);
      } catch (err) {
        this._log('error', `Event handler error [${event}]:`, err);
      }
    }
  }

  // ==================== 生命周期管理 ====================

  private _setLifecycle(phase: ClientLifecycle): void {
    const prev = this._lifecycle;
    if (prev === phase) return;
    this._lifecycle = phase;
    this._log('info', `lifecycle: ${prev} → ${phase}`);
    this._emit('lifecycle-change', phase, prev);
  }

  private _log(level: LogLevel, ...args: unknown[]): void {
    if (LOG_LEVELS[level] > this._logLevel) return;
    const prefix = `[DPRoomSDK][${level.toUpperCase()}]`;
    switch (level) {
      case 'error':
        console.error(prefix, ...args);
        break;
      case 'warn':
        console.warn(prefix, ...args);
        break;
      default:
        console.log(prefix, ...args);
    }
  }

  // ==================== 一键接入 ====================

  async initWithTask(taskId: string): Promise<void> {
    if (!this._apiService) {
      throw new Error(
        'accessToken is required for initWithTask(). ' +
        'The SDK has no API baseUrl — either build with APP_ENV or pass baseUrl in constructor options.',
      );
    }
    if (this._lifecycle === 'destroyed') {
      throw new Error('Client has been destroyed and cannot be reused.');
    }

    if (this._initTask) {
      this._log('warn', 'initWithTask() already in progress, ignoring duplicate call');
      return this._initTask;
    }

    const task = this._doInitWithTask(taskId);
    this._initTask = task;

    try {
      await task;
    } finally {
      this._initTask = null;
    }
  }

  private async _doInitWithTask(taskId: string): Promise<void> {
    this._log('info', `initWithTask: ${taskId}`);
    this._lastTaskId = taskId;
    this._emit('init-progress', 'fetching', '正在获取任务配置...');

    if (this._roomId) {
      this._log('info', `Previous room ${this._roomId} still exists, exiting first`);
      try { await this.exitRoom(); } catch { /* best-effort */ }
    }
    if (this._room) {
      try { await this.disconnect(); } catch { /* best-effort */ }
    }

    try {
      const [dpData, hotWords, roomData, appInfo] = await Promise.all([
        this._apiService!.getTaskDetail(taskId),
        this._apiService!.getHotWords(),
        this._apiService!.enterRoom(taskId),
        this._apiService!.getAppInfo(),
      ]);

      this._roomId = roomData.room_id;
      DPRoomClient._saveRoomId(this._roomId);
      this._emit('init-progress', 'connecting', '正在连接房间...');

      const driverConfig: DriverConfig = {
        rtc_addr: roomData.rtc_config.url,
        rtc_token: roomData.rtc_config.token,
        token:
          typeof this._options.accessToken === 'function'
            ? this._options.accessToken()
            : this._options.accessToken || '',
      };

      this._log('debug', '[AUTH DEBUG] driverConfig.token:', JSON.stringify(driverConfig.token));
      this._log('debug', '[AUTH DEBUG] driverConfig.rtc_token:', JSON.stringify(driverConfig.rtc_token));
      this._log('debug', '[AUTH DEBUG] driverConfig.rtc_addr:', JSON.stringify(driverConfig.rtc_addr));
      this._log('debug', '[AUTH DEBUG] options.accessToken type:', typeof this._options.accessToken);

      await this.connect(driverConfig.rtc_addr, driverConfig.rtc_token);
      this._emit('init-progress', 'initializing', '正在初始化数字人...');
      await this.sendInitMessage(dpData, driverConfig, appInfo, { hotWords: hotWords.asr_words, liveMode: this._options.liveMode });

      if (!this._isRoomReady) {
        this._emit('init-progress', 'waiting', '等待数字人就绪...');
        await this._waitForReady();
      }

      this._emit('init-progress', 'ready', '初始化完成');
      this._log('info', `initWithTask completed, roomId: ${this._roomId}`);
    } catch (err) {
      if (this._roomId) {
        this._log('info', `initWithTask failed, cleaning up room: ${this._roomId}`);
        try { await this.exitRoom(); } catch { /* best-effort */ }
      }

      const errMsg = err instanceof Error ? err.message : String(err);
      const apiCode = err instanceof ApiError ? err.code : 0;

      const error: RoomErrorData = {
        message: errMsg || 'initWithTask 失败',
        source: 'api',
        code: apiCode,
        phase: 'api',
        retriable: apiCode === 51207,
      };
      this._emit('error', error);
      throw err;
    }
  }

  private _waitForReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };

      const timeout = setTimeout(() => {
        cleanup();
        if (this._readyTimeoutPolicy === 'reject') {
          reject(new Error(`Room ready timeout after ${this._readyTimeoutMs}ms`));
        } else {
          this._log('warn', `initWithTask: ready timeout (${this._readyTimeoutMs}ms), resolving anyway`);
          resolve();
        }
      }, this._readyTimeoutMs);

      const cleanup = () => {
        this.off('room-ready', onReady);
        clearTimeout(timeout);
      };

      this.once('room-ready', onReady);
    });
  }

  // ==================== 连接管理 ====================

  async connect(url: string, token: string): Promise<void> {
    if (this._lifecycle === 'destroyed') {
      throw new Error('Client has been destroyed and cannot be reused.');
    }

    if (this._connectTask) {
      switch (this._connectPolicy) {
        case 'ignore':
          this._log('warn', 'connect() already in progress, ignoring duplicate call');
          return;

        case 'queue':
          this._log('info', 'connect() already in progress, queuing');
          return new Promise<void>((resolve, reject) => {
            this._pendingConnect = { url, token, resolve, reject };
          });

        case 'cancel-previous':
          this._log('info', 'connect() cancelling previous connection');
          await this.disconnect();
          break;
      }
    }

    const task = this._doConnect(url, token);
    this._connectTask = task;

    try {
      await task;
    } finally {
      this._connectTask = null;
      if (this._pendingConnect) {
        const { url: nextUrl, token: nextToken, resolve, reject } = this._pendingConnect;
        this._pendingConnect = null;
        this.connect(nextUrl, nextToken).then(resolve, reject);
      }
    }
  }

  private async _doConnect(url: string, token: string): Promise<void> {
    this._setLifecycle('connecting');
    this._lastConnectArgs = { url, token };

    if (this._room) {
      await this.disconnect();
    }

    if (this._options.chromaKey?.enabled) {
      installVP9SDPInterceptor();
      this._log('info', 'VP9 SDP interceptor installed');
    }

    const roomOpts = this._options.roomOptions;
    const chromaKeyActive = !!this._options.chromaKey?.enabled;
    const roomConfig: Record<string, unknown> = {
      adaptiveStream: roomOpts?.adaptiveStream ?? (chromaKeyActive ? false : true),
      dynacast: roomOpts?.dynacast ?? false,
      singlePeerConnection: roomOpts?.singlePeerConnection ?? false,
      publishDefaults: {
        videoCodec: (roomOpts?.publishDefaults?.videoCodec as any) ?? 'vp9',
        simulcast: roomOpts?.publishDefaults?.simulcast ?? false,
        backupCodec: roomOpts?.publishDefaults?.backupCodec ?? false,
      },
    };

    if (chromaKeyActive) {
      (roomConfig as any).preferredSubscribeCodecs = ['vp9'];
      this._log('info', 'Room config: preferredSubscribeCodecs = [vp9]');
    }

    const newRoom = new Room(roomConfig as any);

    this._registerRoomEvents(newRoom);
    this._room = newRoom;

    try {
      await newRoom.connect(url, token, {
        autoSubscribe: roomOpts?.autoSubscribe ?? true,
      });
    } catch (err) {
      this._room = null;
      this._setLifecycle('created');
      const error: RoomErrorData = {
        message: err instanceof Error ? err.message : '连接失败',
        source: 'connection',
        code: 0,
        phase: 'connect',
        retriable: true,
      };
      this._emit('error', error);
      throw err;
    }
  }

  private _registerRoomEvents(room: Room): void {
    room.on(RoomEvent.Connected, () => {
      this._isConnected = true;
      this._setLifecycle('connected');
      this._emit('connected');

      const remoteParticipants = Array.from(room.remoteParticipants.values());
      if (remoteParticipants.length > 0) {
        for (const p of remoteParticipants) {
          this._setupParticipantTracks(p);
        }
        this._handleDriverOnline();
      }
    });

    room.on(RoomEvent.Reconnecting, () => {
      this._log('info', 'LiveKit transport reconnecting...');
      this._setLifecycle('reconnecting');
      this._emit('reconnecting');
      this._emit('reconnect-attempt', {
        attempt: 1,
        maxAttempts: 1,
        remaining: 0,
        level: 'transport',
      });
    });

    room.on(RoomEvent.Reconnected, () => {
      this._log('info', 'LiveKit transport reconnected');
      this._isConnected = true;
      this._setLifecycle('ready');
      this._emit('reconnected', { level: 'transport', attempts: 1 });

      if (this._lastInitArgs && this._isConnected) {
        this._log('info', 'Re-sending init message after transport reconnect');
        const { dpData, driverConfig, appInfo, options } = this._lastInitArgs;
        this.sendInitMessage(dpData, driverConfig, appInfo, options);
      }
    });

    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      this._isConnected = false;
      this._isDriverOnline = false;
      this._isRoomReady = false;
      this._hasVideoTrack = false;
      this._initAckReceived = false;
      this.stopPerformanceMonitor();

      this._emit('disconnected', reason);

      if (reason !== undefined && reason !== DisconnectReason.CLIENT_INITIATED) {
        this._emit('abnormal-disconnect', reason);
        this._setLifecycle('created');
        this._handleAbnormalDisconnect(reason);
      }
    });

    room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
      this._setupParticipantTracks(p);
      this._handleDriverOnline();
      this._emit('participant-connected', p);
    });

    room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
      this._handleDriverOffline();
      this._emit('participant-disconnected', p);
    });

    room.on(RoomEvent.TrackSubscribed, (track: Track, pub: unknown, p: RemoteParticipant) => {
      this._log('debug', `Track subscribed: ${track.kind} from ${p.identity}`);

      if (track.kind === 'video' && this._options.chromaKey?.enabled) {
        this._trySetSubscribeVP9(pub);
      }

      if (track.kind === 'video') {
        this._hasVideoTrack = true;
        this._checkReadyConditions();
      }
      this._emit('track-subscribed', track, p);
    });

    room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
      this._handleDataReceived(payload);
    });
  }

  async exitRoom(): Promise<void> {
    const roomId = this._roomId || DPRoomClient._loadRoomId();
    if (!roomId) {
      this._log('warn', 'exitRoom: no roomId, skip');
      return;
    }
    if (!this._apiService) {
      this._log('warn', 'exitRoom: no apiService, skip');
      return;
    }

    this._roomId = null;
    DPRoomClient._clearRoomId();

    try {
      await this._apiService.exitRoom(roomId);
      this._log('info', `exitRoom success: ${roomId}`);
    } catch (err) {
      this._log('warn', `exitRoom failed: ${roomId}`, err);
      throw err;
    }
  }

  async disconnect(options?: { clearReconnectState?: boolean }): Promise<void> {
    await this.disableMicrophone();
    this.stopPerformanceMonitor();
    this._clearReadyTimers();
    if (this._room) {
      await this._room.disconnect();
      this._room = null;
    }
    this._isConnected = false;
    this._isDriverOnline = false;
    this._isRoomReady = false;
    this._hasVideoTrack = false;
    this._initAckReceived = false;
    this._stateMachine.reset();

    if (options?.clearReconnectState) {
      this._lastInitArgs = null;
      this._lastTaskId = null;
      this._lastConnectArgs = null;
    }

    if (this._lifecycle !== 'destroyed') {
      this._setLifecycle('created');
    }
  }

  getRoom(): Room | null {
    return this._room;
  }

  // ==================== 消息发送 ====================

  async sendInitMessage(
    dpData: DPData,
    driverConfig: DriverConfig,
    appInfo: AppInfo,
    options: InitMessageOptions = {},
  ): Promise<boolean> {
    if (!this._isConnected || !this._room) {
      this._emit('error', {
        message: 'Room is not connected. Call connect() first.',
        source: 'connection',
        code: 0,
        phase: 'init',
      });
      return false;
    }
    this._lastInitArgs = { dpData, driverConfig, appInfo, options };
    const message = createInitMessage(dpData, driverConfig, appInfo, options);
    this._log('debug', '[AUTH DEBUG] init message auth:', JSON.stringify((message as any).data?.auth));
    const success = await this.sendMessage(message);
    if (success) {
      this._isRoomReady = false;
      this._initAckReceived = false;
      this._hasVideoTrack = false;
      this._setLifecycle('initializing');
      this._startRoomReadyCheck();
    }
    return success;
  }

  async sendText(text: string): Promise<boolean> {
    return this.sendCommand('input', { data: text, req_id: `req-${Date.now()}` });
  }

  async sendStreamText(text: string, sessionId: string, isEnd: boolean): Promise<boolean> {
    return this.sendMessage({
      cmd: 'input',
      data: text,
      session_id: sessionId,
      is_end: isEnd,
    });
  }

  async sendCommand(
    cmd: 'wake' | 'sleep' | 'break' | 'input' | 'face_id' | 'init',
    payload?: Record<string, unknown>,
  ): Promise<boolean> {
    return this.sendMessage({ cmd, ...payload });
  }

  async updateDriverToken(token: string): Promise<boolean> {
    return this.sendMessage({ cmd: 'init', data: { token } });
  }

  async sendMessage(message: unknown): Promise<boolean> {
    if (!this._room || !this._room.localParticipant) {
      this._log('warn', 'Cannot send message: room or localParticipant not available');
      return false;
    }

    const bytes = new TextEncoder().encode(JSON.stringify(message));

    for (let attempt = 0; attempt <= MESSAGE_SEND_MAX_RETRIES; attempt++) {
      try {
        await this._room.localParticipant.publishData(bytes, { reliable: true });
        this._log('debug', 'Message sent:', message);
        return true;
      } catch (err) {
        if (attempt < MESSAGE_SEND_MAX_RETRIES) {
          this._log('warn', `Message send failed (attempt ${attempt + 1}/${MESSAGE_SEND_MAX_RETRIES + 1}), retrying...`);
          await new Promise((r) => setTimeout(r, MESSAGE_SEND_RETRY_DELAY_MS));
        } else {
          this._log('error', 'Failed to send message after retries:', err);
          return false;
        }
      }
    }
    return false;
  }

  // ==================== 麦克风 ====================

  async enableMicrophone(deviceId?: string): Promise<void> {
    if (!this._room) {
      this._emit('error', {
        message: 'Room is not connected. Call connect() first.',
        source: 'microphone',
        code: 0,
        phase: 'runtime',
      });
      return;
    }

    await this.disableMicrophone();

    const constraints: MediaTrackConstraints = {
      echoCancellation: this._audioConfig.echoCancellation,
      noiseSuppression: this._audioConfig.noiseSuppression,
      autoGainControl: this._audioConfig.autoGainControl,
      sampleRate: this._audioConfig.sampleRate,
      channelCount: this._audioConfig.channelCount,
      sampleSize: this._audioConfig.sampleSize,
      ...(deviceId && { deviceId: { ideal: deviceId } }),
    };

    try {
      const track = await createLocalAudioTrack(constraints);
      this._audioTrack = track;
      await this._room.localParticipant.publishTrack(track);
      this._registerTrackEndedListener(track);
      this._setMicState(MicrophoneState.Active);
      this._log('info', 'Microphone enabled' + (deviceId ? ` (device: ${deviceId})` : ''));
    } catch (err) {
      this._audioTrack = null;
      this._setMicState(MicrophoneState.Error);
      this._emit('error', this._classifyMicError(err));
    }
  }

  async disableMicrophone(): Promise<void> {
    if (!this._audioTrack) return;
    this._removeTrackEndedListener();
    if (this._room) {
      try {
        await this._room.localParticipant.unpublishTrack(this._audioTrack);
      } catch {
        /* best-effort */
      }
    }
    try {
      this._audioTrack.stop();
    } catch {
      /* best-effort */
    }
    this._audioTrack = null;
    this._setMicState(MicrophoneState.Idle);
    this._log('info', 'Microphone disabled');
  }

  muteMicrophone(): void {
    this._audioTrack?.mute();
  }

  unmuteMicrophone(): void {
    this._audioTrack?.unmute();
  }

  async switchMicrophoneDevice(deviceId: string): Promise<void> {
    if (!this._room) {
      this._emit('error', {
        message: 'Room is not connected. Call connect() first.',
        source: 'microphone',
        code: 0,
        phase: 'runtime',
      });
      return;
    }

    const constraints: MediaTrackConstraints = {
      echoCancellation: this._audioConfig.echoCancellation,
      noiseSuppression: this._audioConfig.noiseSuppression,
      autoGainControl: this._audioConfig.autoGainControl,
      sampleRate: this._audioConfig.sampleRate,
      channelCount: this._audioConfig.channelCount,
      sampleSize: this._audioConfig.sampleSize,
      deviceId: { ideal: deviceId },
    };

    try {
      const newTrack = await createLocalAudioTrack(constraints);
      await this._room.localParticipant.publishTrack(newTrack);

      // New track published successfully — tear down the old one
      this._removeTrackEndedListener();
      if (this._audioTrack) {
        try {
          await this._room.localParticipant.unpublishTrack(this._audioTrack);
        } catch {
          /* best-effort */
        }
        try {
          this._audioTrack.stop();
        } catch {
          /* best-effort */
        }
      }

      this._audioTrack = newTrack;
      this._registerTrackEndedListener(newTrack);
      this._setMicState(MicrophoneState.Active);
      this._log('info', `Microphone switched to device: ${deviceId}`);
    } catch (err) {
      this._emit('error', this._classifyMicError(err));
    }
  }

  async getMicrophoneDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(d => d.kind === 'audioinput');
    } catch (err) {
      this._log('warn', 'Failed to enumerate microphone devices:', err);
      return [];
    }
  }

  getAudioTrack(): LocalAudioTrack | null {
    return this._audioTrack;
  }

  isMicrophoneEnabled(): boolean {
    return this._audioTrack !== null;
  }

  isMicrophoneMuted(): boolean {
    return this._audioTrack?.isMuted ?? true;
  }

  private _setMicState(state: MicrophoneState): void {
    const prev = this._micState;
    if (prev === state) return;
    this._micState = state;
    this._log('debug', `microphoneState: ${prev} → ${state}`);
    this._emit('microphone-state-change', state, prev);
  }

  private _registerTrackEndedListener(track: LocalAudioTrack): void {
    this._removeTrackEndedListener();
    this._trackEndedHandler = () => this._handleMicrophoneTrackEnded();
    track.mediaStreamTrack.addEventListener('ended', this._trackEndedHandler);
  }

  private _removeTrackEndedListener(): void {
    if (this._trackEndedHandler && this._audioTrack) {
      this._audioTrack.mediaStreamTrack.removeEventListener('ended', this._trackEndedHandler);
    }
    this._trackEndedHandler = null;
  }

  private async _handleMicrophoneTrackEnded(): Promise<void> {
    this._log('warn', 'Microphone track ended unexpectedly (device unplugged or permission revoked)');

    this._removeTrackEndedListener();

    if (this._room && this._audioTrack) {
      try {
        await this._room.localParticipant.unpublishTrack(this._audioTrack);
      } catch {
        /* best-effort */
      }
    }

    if (this._audioTrack) {
      try {
        this._audioTrack.stop();
      } catch {
        /* best-effort */
      }
      this._audioTrack = null;
    }

    this._setMicState(MicrophoneState.Error);
    this._emit('error', {
      message: '麦克风设备已断开或权限被撤销',
      source: 'microphone',
      code: 0,
      phase: 'runtime',
      retriable: true,
    });
  }

  private _classifyMicError(err: unknown): RoomErrorData {
    if (err instanceof Error) {
      const name = err.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        return {
          message: '麦克风权限被拒绝，请在浏览器设置中允许使用麦克风',
          source: 'microphone',
          code: 0,
          phase: 'runtime',
          retriable: false,
        };
      }
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return {
          message: '未找到麦克风设备，请连接麦克风后重试',
          source: 'microphone',
          code: 0,
          phase: 'runtime',
          retriable: true,
        };
      }
      if (name === 'NotReadableError' || name === 'AbortError') {
        return {
          message: '麦克风设备被占用，请关闭其他使用麦克风的应用后重试',
          source: 'microphone',
          code: 0,
          phase: 'runtime',
          retriable: true,
        };
      }
      if (err.message?.includes('publish')) {
        return {
          message: '发布麦克风轨道失败，请重试',
          source: 'microphone',
          code: 0,
          phase: 'runtime',
          retriable: true,
        };
      }
      return {
        message: `麦克风采集失败: ${err.message}`,
        source: 'microphone',
        code: 0,
        phase: 'runtime',
        retriable: true,
      };
    }
    return {
      message: '麦克风未知错误',
      source: 'microphone',
      code: 0,
      phase: 'runtime',
      retriable: true,
    };
  }

  // ==================== 轨道管理（低级） ====================

  async publishTrack(track: LocalTrack): Promise<void> {
    if (!this._room) throw new Error('Room is not connected.');
    await this._room.localParticipant.publishTrack(track);
  }

  async unpublishTrack(track: LocalTrack): Promise<void> {
    if (!this._room) return;
    await this._room.localParticipant.unpublishTrack(track);
  }

  // ==================== 状态控制 ====================

  getState(): RoomState {
    return this._stateMachine.state;
  }

  startInteraction(): void {
    if (!this._isConnected || !this._room) {
      this._emit('error', {
        message: 'Room is not connected. Call connect() first.',
        source: 'connection',
        code: 0,
        phase: 'runtime',
      });
      return;
    }
    this.wake();
  }

  endInteraction(): void {
    this.sleep();
  }

  wake(): void {
    if (this._stateMachine.state === RoomState.Standby) {
      this.sendCommand('wake');
    }
  }

  sleep(): void {
    this.sendCommand('sleep');
    this._stateMachine.transition(RoomState.Standby);
  }

  interrupt(): void {
    if (this._stateMachine.state === RoomState.Responding) {
      this.sendCommand('break');
      this._stateMachine.transition(RoomState.Awakened);
    }
  }

  // ==================== 重连 ====================

  get isReconnecting(): boolean {
    return this._isReconnecting;
  }

  async reconnect(options?: { url?: string; token?: string }): Promise<void> {
    if (this._lifecycle === 'destroyed') {
      throw new Error('Client has been destroyed and cannot be reused.');
    }
    if (this._isReconnecting) {
      this._log('warn', 'reconnect() already in progress, ignoring');
      return;
    }

    if (options?.url && options?.token) {
      await this._doFullReconnect(options.url, options.token);
    } else if (this._lastTaskId && this._apiService) {
      await this._doTaskReconnect();
    } else if (this._reconnectPolicy.tokenProvider) {
      const creds = await this._reconnectPolicy.tokenProvider();
      await this._doFullReconnect(creds.url, creds.token);
    } else if (this._lastConnectArgs) {
      await this._doFullReconnect(this._lastConnectArgs.url, this._lastConnectArgs.token);
    } else {
      throw new Error(
        'Cannot reconnect: no taskId, tokenProvider, or previous connection args available. ' +
        'Provide url/token parameters or configure reconnectPolicy.tokenProvider.',
      );
    }
  }

  private _cancelReconnect(): void {
    if (this._reconnectAbort) {
      this._reconnectAbort.abort();
      this._reconnectAbort = null;
    }
    this._isReconnecting = false;
  }

  private _mapDisconnectReason(reason?: DisconnectReason): ReconnectFailReason | null {
    if (reason === undefined) return null;
    switch (reason) {
      case DisconnectReason.CLIENT_INITIATED:
        return null;
      case DisconnectReason.PARTICIPANT_REMOVED:
        return 'KICKED';
      case DisconnectReason.ROOM_DELETED:
        return 'ROOM_CLOSED';
      case DisconnectReason.DUPLICATE_IDENTITY:
        return 'DUPLICATE_SESSION';
      default:
        return null;
    }
  }

  private _handleAbnormalDisconnect(reason?: DisconnectReason): void {
    const failReason = this._mapDisconnectReason(reason);

    if (failReason) {
      const messageMap: Record<string, string> = {
        KICKED: '已被移出房间',
        ROOM_CLOSED: '房间已关闭或到期',
        DUPLICATE_SESSION: '账号在其他设备登录，当前连接已断开',
      };
      const codeMap: Record<string, number> = {
        KICKED: 4001,
        ROOM_CLOSED: 4002,
        DUPLICATE_SESSION: 4003,
      };
      const error: RoomErrorData = {
        message: messageMap[failReason] ?? '连接异常断开',
        source: 'connection',
        code: codeMap[failReason] ?? 0,
        phase: 'reconnect',
        reason: failReason,
        retriable: false,
      };
      this._emit('error', error);
      this._emit('reconnect-failed', error);
      return;
    }

    this._micWasActiveBeforeDisconnect = this._micState === MicrophoneState.Active;

    if (this._reconnectPolicy.autoReconnect && this._reconnectPolicy.fullReconnect) {
      this._startAutoReconnect();
    }
  }

  private async _startAutoReconnect(): Promise<void> {
    if (this._isReconnecting) return;

    this._log('info', 'Starting auto reconnect...');

    try {
      await this.reconnect();
    } catch (err) {
      this._log('error', 'Auto reconnect ultimately failed:', err);
    }
  }

  private async _doTaskReconnect(): Promise<void> {
    const taskId = this._lastTaskId!;
    const api = this._apiService!;
    const { maxAttempts, baseDelayMs, maxDelayMs, attemptTimeoutMs } = this._reconnectPolicy;

    this._isReconnecting = true;
    this._reconnectAbort = new AbortController();
    const signal = this._reconnectAbort.signal;

    this._setLifecycle('reconnecting');
    this._emit('reconnecting');

    let lastFailReason: ReconnectFailReason = 'UNKNOWN';
    let lastErrorMsg = '';

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (signal.aborted) {
        this._isReconnecting = false;
        const error: RoomErrorData = {
          message: '客户端已销毁，重连已取消',
          source: 'connection',
          code: 4009,
          phase: 'reconnect',
          reason: 'DESTROYED',
          retriable: false,
        };
        this._emit('reconnect-failed', error);
        this._emit('error', error);
        return;
      }

      this._emit('reconnect-attempt', {
        attempt,
        maxAttempts,
        remaining: maxAttempts - attempt,
        level: 'application',
      });
      this._log('info', `Reconnect attempt ${attempt}/${maxAttempts}`);

      if (attempt > 1) {
        const delay = Math.min(baseDelayMs * Math.pow(2, attempt - 2), maxDelayMs);
        const jitter = Math.random() * delay * 0.2;
        await new Promise((r) => setTimeout(r, delay + jitter));
      }

      try {
        const result = await Promise.race([
          this._doTaskReconnectAttempt(taskId, api),
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('重连超时')), attemptTimeoutMs);
          }),
          new Promise<never>((_, reject) => {
            const onAbort = () => reject(new Error('reconnect cancelled'));
            signal.addEventListener('abort', onAbort, { once: true });
          }),
        ]);
        void result;

        this._isReconnecting = false;
        this._reconnectAbort = null;
        this._emit('reconnected', { level: 'application', attempts: attempt });
        this._log('info', `Reconnect succeeded after ${attempt} attempt(s)`);

        if (this._micWasActiveBeforeDisconnect) {
          this._micWasActiveBeforeDisconnect = false;
          this._log('info', 'Restoring microphone after reconnect');
          await this.enableMicrophone();
        }
        return;
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        lastErrorMsg = errMsg;

        if (errMsg.includes('401') || errMsg.includes('Unauthorized')) {
          lastFailReason = 'AUTH_TOKEN_EXPIRED';
          const error: RoomErrorData = {
            message: '登录凭证已过期，请重新登录',
            source: 'api',
            code: 401,
            phase: 'reconnect',
            reason: 'AUTH_TOKEN_EXPIRED',
            retriable: false,
          };
          this._isReconnecting = false;
          this._reconnectAbort = null;
          this._emit('reconnect-failed', error);
          this._emit('error', error);
          return;
        }

        if (errMsg.includes('cancelled') || errMsg.includes('destroyed') || signal.aborted) {
          this._isReconnecting = false;
          const error: RoomErrorData = {
            message: '客户端已销毁，重连已取消',
            source: 'connection',
            code: 4009,
            phase: 'reconnect',
            reason: 'DESTROYED',
            retriable: false,
          };
          this._emit('reconnect-failed', error);
          this._emit('error', error);
          return;
        }

        if (errMsg.includes('超时')) {
          lastFailReason = 'TIMEOUT';
        } else {
          lastFailReason = 'NETWORK_ERROR';
        }

        this._log('warn', `Reconnect attempt ${attempt} failed: ${errMsg}`);
      }
    }

    this._isReconnecting = false;
    this._reconnectAbort = null;

    const error: RoomErrorData = {
      message: `重连失败，已达最大尝试次数(${maxAttempts}次)，最后失败原因: ${lastErrorMsg}`,
      source: 'connection',
      code: 4010,
      phase: 'reconnect',
      reason: 'MAX_ATTEMPTS',
      retriable: true,
    };
    this._setLifecycle('created');
    this._emit('reconnect-failed', error);
    this._emit('error', error);
  }

  private async _doTaskReconnectAttempt(taskId: string, api: ApiService): Promise<void> {
    if (this._room) {
      try { await this._room.disconnect(); } catch { /* best-effort */ }
      this._room = null;
    }

    const [dpData, hotWords, roomData, appInfo] = await Promise.all([
      api.getTaskDetail(taskId),
      api.getHotWords(),
      api.enterRoom(taskId),
      api.getAppInfo(),
    ]);

    this._roomId = roomData.room_id;
    DPRoomClient._saveRoomId(this._roomId);

    const driverConfig: DriverConfig = {
      rtc_addr: roomData.rtc_config.url,
      rtc_token: roomData.rtc_config.token,
      token:
        typeof this._options.accessToken === 'function'
          ? this._options.accessToken()
          : this._options.accessToken || '',
    };

    try {
      await this.connect(driverConfig.rtc_addr, driverConfig.rtc_token);
      await this.sendInitMessage(dpData, driverConfig, appInfo, { hotWords: hotWords.asr_words, liveMode: this._options.liveMode });

      if (!this._isRoomReady) {
        await this._waitForReady();
      }
    } catch (err) {
      if (this._roomId) {
        this._log('info', `Reconnect attempt failed, cleaning up room: ${this._roomId}`);
        try { await this.exitRoom(); } catch { /* best-effort */ }
      }
      throw err;
    }
  }

  private async _doFullReconnect(url: string, token: string): Promise<void> {
    const { maxAttempts, baseDelayMs, maxDelayMs, attemptTimeoutMs } = this._reconnectPolicy;

    this._isReconnecting = true;
    this._reconnectAbort = new AbortController();
    const signal = this._reconnectAbort.signal;

    this._setLifecycle('reconnecting');
    this._emit('reconnecting');

    let lastFailReason: ReconnectFailReason = 'UNKNOWN';
    let lastErrorMsg = '';

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (signal.aborted) {
        this._isReconnecting = false;
        const error: RoomErrorData = {
          message: '客户端已销毁，重连已取消',
          source: 'connection',
          code: 4009,
          phase: 'reconnect',
          reason: 'DESTROYED',
          retriable: false,
        };
        this._emit('reconnect-failed', error);
        this._emit('error', error);
        return;
      }

      this._emit('reconnect-attempt', {
        attempt,
        maxAttempts,
        remaining: maxAttempts - attempt,
        level: 'application',
      });
      this._log('info', `Reconnect attempt ${attempt}/${maxAttempts}`);

      if (attempt > 1) {
        const delay = Math.min(baseDelayMs * Math.pow(2, attempt - 2), maxDelayMs);
        const jitter = Math.random() * delay * 0.2;
        await new Promise((r) => setTimeout(r, delay + jitter));
      }

      try {
        await Promise.race([
          this._doFullReconnectAttempt(url, token),
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('重连超时')), attemptTimeoutMs);
          }),
          new Promise<never>((_, reject) => {
            const onAbort = () => reject(new Error('reconnect cancelled'));
            signal.addEventListener('abort', onAbort, { once: true });
          }),
        ]);

        this._isReconnecting = false;
        this._reconnectAbort = null;
        this._emit('reconnected', { level: 'application', attempts: attempt });
        this._log('info', `Reconnect succeeded after ${attempt} attempt(s)`);

        if (this._micWasActiveBeforeDisconnect) {
          this._micWasActiveBeforeDisconnect = false;
          this._log('info', 'Restoring microphone after reconnect');
          await this.enableMicrophone();
        }
        return;
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        lastErrorMsg = errMsg;

        if (errMsg.includes('cancelled') || errMsg.includes('destroyed') || signal.aborted) {
          this._isReconnecting = false;
          const error: RoomErrorData = {
            message: '客户端已销毁，重连已取消',
            source: 'connection',
            code: 4009,
            phase: 'reconnect',
            reason: 'DESTROYED',
            retriable: false,
          };
          this._emit('reconnect-failed', error);
          this._emit('error', error);
          return;
        }

        if (errMsg.includes('超时')) {
          lastFailReason = 'TIMEOUT';
        } else {
          lastFailReason = 'NETWORK_ERROR';
        }

        this._log('warn', `Reconnect attempt ${attempt} failed: ${errMsg}`);
      }
    }

    this._isReconnecting = false;
    this._reconnectAbort = null;

    const error: RoomErrorData = {
      message: `重连失败，已达最大尝试次数(${maxAttempts}次)，最后失败原因: ${lastErrorMsg}`,
      source: 'connection',
      code: 4010,
      phase: 'reconnect',
      reason: 'MAX_ATTEMPTS',
      retriable: true,
    };
    this._setLifecycle('created');
    this._emit('reconnect-failed', error);
    this._emit('error', error);
  }

  private async _doFullReconnectAttempt(url: string, token: string): Promise<void> {
    if (this._room) {
      try { await this._room.disconnect(); } catch { /* best-effort */ }
      this._room = null;
    }

    await this.connect(url, token);

    if (this._lastInitArgs) {
      const { dpData, driverConfig, appInfo, options } = this._lastInitArgs;
      await this.sendInitMessage(dpData, driverConfig, appInfo, options);
      if (!this._isRoomReady) {
        await this._waitForReady();
      }
    }
  }

  // ==================== 销毁 ====================

  async destroy(): Promise<void> {
    if (this._lifecycle === 'destroyed') return;

    this._removeBeforeUnload();
    this._cancelReconnect();

    try {
      await this.exitRoom();
    } catch { /* best-effort */ }

    await this.disconnect({ clearReconnectState: true });
    this.stopPerformanceMonitor();
    this._clearReadyTimers();
    this._destroyChromaKey();
    this._stateMachine.destroy();
    this._listeners = {};
    this._setLifecycle('destroyed');
  }

  /**
   * 页面关闭时的同步清理：用 sendBeacon 通知后端离开房间，
   * 同步断开 LiveKit 连接。beforeunload 中无法使用 async。
   */
  private _syncDestroy(): void {
    if (this._lifecycle === 'destroyed') return;

    const roomId = this._roomId || DPRoomClient._loadRoomId();
    if (roomId && this._apiService) {
      const baseUrl = (this._apiService as any)._baseUrl;
      const clientId = localStorage.getItem('__dp_sdk_client_id__') || '';
      const url = `${baseUrl}/sdk/room/exit`;
      const body = JSON.stringify({ client_id: clientId, room_id: roomId });
      try {
        navigator.sendBeacon(
          url,
          new Blob([body], { type: 'application/json' }),
        );
      } catch { /* best-effort */ }
    }

    DPRoomClient._clearRoomId();

    if (this._room) {
      try { this._room.disconnect(); } catch { /* best-effort */ }
      this._room = null;
    }

    this._setLifecycle('destroyed');
  }

  private _removeBeforeUnload(): void {
    if (this._boundBeforeUnload && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this._boundBeforeUnload);
      this._boundBeforeUnload = null;
    }
  }

  // ==================== 内部方法 ====================

  private _setupParticipantTracks(participant: RemoteParticipant): void {
    participant.trackPublications.forEach((pub) => {
      if (pub.track) {
        this._emit('track-subscribed', pub.track, participant);
        if (pub.track.kind === 'video') {
          this._hasVideoTrack = true;
        }
      }
    });
  }

  private async _handleDriverOnline(): Promise<void> {
    this._isDriverOnline = true;
    this._log('info', 'Driver online');
    this._emit('driver-online');

    if (this._lastInitArgs && this._isConnected) {
      this._log('info', 'Re-sending init message to reconnected driver');
      const { dpData, driverConfig, appInfo, options } = this._lastInitArgs;
      await this.sendInitMessage(dpData, driverConfig, appInfo, options);
    }
  }

  private _handleDriverOffline(): void {
    this._isDriverOnline = false;
    this._isRoomReady = false;
    this._initAckReceived = false;
    this._hasVideoTrack = false;
    this._log('info', 'Driver offline');
    this._emit('driver-offline');
  }

  private _clearReadyTimers(): void {
    if (this._readyTimeout) {
      clearTimeout(this._readyTimeout);
      this._readyTimeout = null;
    }
    if (this._readyDelay) {
      clearTimeout(this._readyDelay);
      this._readyDelay = null;
    }
  }

  private _startRoomReadyCheck(): void {
    this._clearReadyTimers();
    this._readyTimeout = setTimeout(() => {
      if (!this._isRoomReady) {
        this._log('warn', 'Room ready timeout, force marking as ready');
        this._markRoomAsReady();
      }
    }, this._readyTimeoutMs);
  }

  private _checkReadyConditions(): void {
    if (this._isRoomReady) return;

    const initOk = !this._readyConditions.initAck || this._initAckReceived;
    const videoOk = !this._readyConditions.videoTrack || this._hasVideoTrack;

    if (initOk && videoOk) {
      this._readyDelay = setTimeout(() => this._markRoomAsReady(), READY_DELAY);
    }
  }

  private _markRoomAsReady(): void {
    if (this._isRoomReady) return;
    this._isRoomReady = true;
    this._clearReadyTimers();
    if (this._stateMachine.state !== RoomState.Standby) {
      this._stateMachine.transition(RoomState.Standby);
    }
    this._setLifecycle('ready');
    this._log('info', 'Room is ready');
    this._emit('room-ready');

    if (this._autoEnableMicrophone) {
      this.enableMicrophone().catch(err => {
        this._log('warn', 'Auto enable microphone failed:', err);
      });
    }
  }

  private _handleDataReceived(payload: Uint8Array): void {
    try {
      const text = new TextDecoder('utf-8').decode(payload);
      const data = JSON.parse(text) as DataChannelMessage;
      this._log('debug', 'DataChannel received:', data);
      this._emit('data-received', data);

      // init finish → 延迟标记为 ready
      if (
        data.operate_type === 'finish' &&
        typeof data.data === 'object' &&
        data.data !== null &&
        (data.data as any).state === 'init'
      ) {
        this._log('info', 'Received init finish acknowledgement');
        this._initAckReceived = true;
        this._checkReadyConditions();
        return;
      }

      // 驱动端确认唤醒
      if (
        data.response_type === 'state' &&
        typeof data.data === 'object' &&
        data.data !== null &&
        (data.data as any).state === 'wake'
      ) {
        this._stateMachine.transition(RoomState.Awakened);
        return;
      }

      // Standby 状态下忽略后续消息（sleep 后的残留消息）
      if (this._stateMachine.state === RoomState.Standby) {
        this._log('debug', 'Ignoring message in Standby state');
        return;
      }

      // 会话结束
      if (
        data.response_type === 'state' &&
        data.operate_type === 'finish' &&
        typeof data.data === 'object' &&
        data.data !== null &&
        (data.data as any).state === 'session'
      ) {
        this._stateMachine.exitConversationSession();
        this._stateMachine.transition(RoomState.Awakened);
        this._emit('session-end');
        return;
      }

      // 错误消息
      if (data.code !== 200 && typeof data.data === 'object' && data.data !== null) {
        const ed = data.data as any;
        let roomError: RoomErrorData;
        if (ed.external_error_data) {
          roomError = {
            message: ed.external_error_data.data || ed.error_msg || '后端服务错误',
            source: 'backend',
            code: ed.external_error_data.code ?? 0,
            phase: 'runtime',
          };
        } else {
          roomError = {
            message: ed.error_msg || data.err_message || '驱动服务错误',
            source: 'driver',
            code: data.code,
            phase: 'runtime',
          };
        }
        this._emit('error', roomError);
        return;
      }

      // ASR 消息
      if (data.response_type === 'asr') {
        if (!this._stateMachine.inConversationSession) {
          this._stateMachine.enterConversationSession();
        }
        this._stateMachine.transition(RoomState.Listening);
        this._emitConversationMessage(data, 'user');
        return;
      }

      // LLM 消息
      if (data.response_type === 'llm') {
        if (!this._stateMachine.inConversationSession) {
          this._stateMachine.enterConversationSession();
        }
        this._stateMachine.transition(RoomState.Responding);
        this._emitConversationMessage(data, 'ai');
        return;
      }
    } catch (err) {
      this._log('debug', 'DataChannel message parse error:', err, payload);
    }
  }

  private _emitConversationMessage(
    data: DataChannelMessage,
    type: 'user' | 'ai',
  ): void {
    let content = '';
    if (typeof data.data === 'string') {
      content = data.data;
    } else if (data.data && typeof data.data === 'object' && 'text' in data.data) {
      content = (data.data as any).text || '';
    }

    const timestamp =
      typeof data.timestamp === 'string'
        ? parseInt(data.timestamp, 10)
        : data.timestamp;

    const message: ConversationMessage = {
      type,
      content,
      timestamp,
      sessionId: data.session_id || `session-${timestamp}`,
      operateType: data.operate_type as 'update' | 'append' | 'finish',
    };

    if (type === 'user') {
      this._emit('asr-message', message);
    } else {
      this._emit('llm-message', message);
    }
  }

  // ==================== VP9 Subscribe Preference ====================

  private _trySetSubscribeVP9(pub: unknown): void {
    const publication = pub as any;
    if (!publication) return;
    try {
      if (typeof publication.setVideoCodec === 'function') {
        publication.setVideoCodec('vp9');
        this._log('info', 'Set VP9 codec preference on remote video publication');
      } else if (typeof publication.preferredCodec !== 'undefined') {
        publication.preferredCodec = 'vp9';
        this._log('info', 'Set preferredCodec = vp9 on remote video publication');
      } else {
        this._log('debug', 'LiveKit SDK does not expose subscribe codec API — relying on SDP interceptor');
      }
    } catch (err) {
      this._log('debug', 'Failed to set subscribe VP9 preference:', err);
    }
  }

  // ==================== Chroma Key ====================

  private _initChromaKey(options: DPRoomClientOptions): void {
    const ck = options.chromaKey!;
    const { enabled: _, ...rendererConfig } = ck;

    this._chromaKeyRenderer = new ChromaKeyRenderer(rendererConfig);

    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    video.style.position = 'absolute';
    video.style.width = '0';
    video.style.height = '0';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';
    document.body.appendChild(video);
    this._hiddenVideoEl = video;

    const canvas = this._chromaKeyRenderer.getCanvas();
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    this._containerEl!.appendChild(canvas);

    this._log('info', 'ChromaKey renderer initialized');
  }

  private _destroyChromaKey(): void {
    if (this._chromaKeyRenderer) {
      this._chromaKeyRenderer.destroy();
      this._chromaKeyRenderer = null;
    }
    if (this._hiddenVideoEl) {
      this._hiddenVideoEl.srcObject = null;
      this._hiddenVideoEl.remove();
      this._hiddenVideoEl = null;
    }
    if (this._nativeVideoEl) {
      this._nativeVideoEl.srcObject = null;
      this._nativeVideoEl.remove();
      this._nativeVideoEl = null;
    }
    this._alphaMode = null;
  }
}
